
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import FloatingUpgradeButton from '@/components/premium/FloatingUpgradeButton';

interface MainContentProps {
  children: React.ReactNode;
}

const MainContent: React.FC<MainContentProps> = ({ children }) => {
  const location = useLocation();
  const path = location.pathname;
  const isMobile = useIsMobile();
  const contentRef = useRef<HTMLDivElement>(null);
  const initialRenderCompleted = useRef(false);
  
  // Special case for pitch guide page to account for sticky nav and to hide upgrade button
  const isPitchGuidePage = path === '/pitch-guide';
  const isLeadCapturePage = path === '/lead-capture';
  
  // Special case for deal-room to ensure proper layout persistence
  const isDealRoom = path === '/deal-room';
  
  // Pages without header
  const pagesWithNoHeader = [
    '/dashboard',
    '/get-help',
    '/settings',
    '/mastering-fundraising',
    '/financial-modeling',
    '/ma-strategies',
    '/fundraising-videos',
    '/pitch-analyzer',
    '/valuation-calculator',
    '/fundraising-stats',
    '/templates',
    '/financial-model-template',
    '/feedback',
    '/company',
    '/'
  ];

  // Check if we're in resources or training pages
  const isResourcesOrTrainingPage = [
    '/mastering-fundraising',
    '/financial-modeling',
    '/ma-strategies',
    '/fundraising-videos',
    '/pitch-analyzer',
    '/valuation-calculator',
    '/fundraising-stats',
    '/templates',
    '/financial-model-template',
    '/settings',
    '/get-help',
    '/feedback',
    '/company'
  ].includes(path);
  
  // Special cases for various pages
  const isDashboard = path === '/dashboard';
  const isFundraisingTimeline = path === '/fundraising-timeline';
  const isInvestorDirectory = path === '/investor-directory';
  const isPitchAnalyzerPage = path === '/pitch-analyzer';
  const isValuationCalculatorPage = path === '/valuation-calculator';
  const isFinancialModelTemplatePage = path === '/financial-model-template';
  const isTemplatesPage = path === '/templates';
  const isFundraisingStatsPage = path === '/fundraising-stats';
  const isMasteringFundraisingPage = path === '/mastering-fundraising';
  const isFinancialModelingPage = path === '/financial-modeling';
  const isMAStrategiesPage = path === '/ma-strategies';
  const isFundraisingVideosPage = path === '/fundraising-videos';
  const isHomePage = path === '/';
  const isSettingsPage = path === '/settings';
  const isGetHelpPage = path === '/get-help';
  const isFeedbackPage = path === '/feedback';
  const isCompanyPage = path === '/company';
  const isManageSubscriptionPage = path === '/manage-subscription';
  
  // Check if we're on an external page that shouldn't show the premium button
  const isExternalPage = [
    '/',                      // Homepage
    '/pricing',               // Pricing page
    '/signin',                // Sign in page
    '/signup',                // Sign up page
    '/onboarding',            // Onboarding page
    '/privacy-policy',        // Privacy policy
    '/terms-and-conditions',  // Terms
    '/cookie-policy',         // Cookie policy
    '/disclaimer',            // Disclaimer
    '/cancellation-guide',    // Cancellation guide
    '/accessibility-statement', // Accessibility
    '/pitch-guide',           // Pitch guide page
    '/lead-capture'           // Lead capture page
  ].includes(path);
  
  // Log for debugging
  useEffect(() => {
    console.log(`MainContent rendered with path: ${path}`);
    console.log('Main content ref:', contentRef.current);
    console.log('Children present:', !!children);
    console.log('Mobile:', isMobile);
    console.log('Is external page:', isExternalPage);
    console.log('Is deal room page:', isDealRoom);
    console.log('Is pitch guide page:', isPitchGuidePage);
    console.log('Is lead capture page:', isLeadCapturePage);
  }, [path, children, isMobile, isExternalPage, isDealRoom, isPitchGuidePage, isLeadCapturePage]);

  // Container styles - ensures full width with proper overflow
  // Add specific class for deal-room to ensure persistent layout
  // Add specific class for pitch-guide to account for sticky nav
  const containerClasses = `
    flex flex-col w-full h-full flex-1 overflow-y-auto 
    bg-gradient-to-br from-background to-muted/30
    ${isDealRoom ? 'deal-room-container' : ''}
    ${isPitchGuidePage || isLeadCapturePage ? 'pitch-guide-container pb-24' : ''}
  `;

  // Content styles with proper padding - removed any constrained widths
  // Add specific padding handling for deal-room and pitch-guide/lead-capture
  const contentClasses = `
    flex flex-col w-full flex-1 min-w-0
    ${isDealRoom ? 'px-0 py-0' : ''}
    ${(isPitchGuidePage || isLeadCapturePage) ? 'pt-0 px-0' : ''} 
    ${isInvestorDirectory || isPitchAnalyzerPage || isValuationCalculatorPage || 
      isFinancialModelTemplatePage || isTemplatesPage || isFundraisingStatsPage || 
      isMasteringFundraisingPage || isFinancialModelingPage || isMAStrategiesPage ||
      isFundraisingVideosPage || isSettingsPage || isGetHelpPage || isFeedbackPage || 
      isCompanyPage || isManageSubscriptionPage ? 'px-0 py-0' : 'p-3 md:px-6'}
    ${isDashboard ? 'md:pt-4' : ''}
  `;

  return (
    <div 
      className={containerClasses} 
      id="main-content-container" 
      data-path={path} 
      data-is-deal-room={isDealRoom ? 'true' : 'false'}
      data-is-pitch-guide={isPitchGuidePage ? 'true' : 'false'}
      data-is-lead-capture={isLeadCapturePage ? 'true' : 'false'}
      ref={contentRef}
    >
      <main className={contentClasses}>
        <div className="w-full h-full flex-1">
          {children}
        </div>
      </main>
      
      {/* Only show floating upgrade button on internal app pages AND not on the pitch guide page or lead capture page */}
      {!isExternalPage && !isPitchGuidePage && !isLeadCapturePage && <FloatingUpgradeButton />}
    </div>
  );
};

export default MainContent;
